import React, { useEffect, useState } from 'react'
import { ActivityIndicator, Keyboard, Platform, ScrollView } from 'react-native'

import {
  DateTimePickerAndroid,
  DateTimePickerEvent,
} from '@react-native-community/datetimepicker'
import { BottomView } from '@zet/common/components/bottomView'
import KeyBoardWrapper from '@zet/common/components/keyboardWrapper'
import LazyHOC from '@zet/common/components/lazyHoc'
import {
  BottomSheet,
  Stack,
  WebDatePicker,
  XStack,
  colors as zetColors,
} from '@zet/design-system'
import { Icon } from '@zet/design-system/icons'
import { Input } from '@zet/design-system/input'
import Pressable from '@zet/design-system/pressable'
import { Select } from '@zet/design-system/select'
import { B1, B2, B5, SH1 } from '@zet/design-system/typography'
import { i18n } from '@zet/localization'
import { NAME_REGEX, PINCODE_REGEX } from '@zet/utils/regularExpression'
import { NAME_FIELD_VALIDATION } from '@zet/utils/validationMessage'
import moment from 'moment'
import { Controller, useForm } from 'react-hook-form'

import {
  useFetchCustomerDetails,
  useFetchFieldsByCriteria,
} from '../../api/hooks'
import useEditedDetails from '../../api/hooks/useEditedDetails'

const EditCustomerDetails = ({
  route,
}: {
  route: { params: { customerId: number } }
}) => {
  const [employmentType, setEmploymentType] = useState<string>('')
  const [isEmploymentSheetVisible, setIsEmploymentSheetVisible] =
    useState<boolean>(false)
  const [annualIncome, setAnnualIncome] = useState<string>('')
  const [salaryType, setSalaryType] = useState<string>('')
  const today = new Date()
  const MIN_ELIGIBLE_DATE = moment().subtract(15, 'years').format('YYYY-MM-DD')

  const [date, setDate] = useState<Date>(new Date(MIN_ELIGIBLE_DATE))

  const { data: prefilledData, isLoading: prefilledDataLoading } =
    useFetchCustomerDetails(route?.params?.customerId)

  const {
    control,
    formState: { errors, isValid },
    getValues,
    setValue,
    unregister,
    setError,
    trigger,
  } = useForm({
    mode: 'onChange',
    shouldUnregister: true,
  })
  useEffect(() => {
    if (prefilledData?.modeOfIncome) {
      if (prefilledData?.modeOfIncome === 'Cash') {
        setSalaryType('Cash')
        setValue('salaryType', 'Cash', {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        })
      } else if (prefilledData?.modeOfIncome === 'Bank') {
        setSalaryType('Bank')
        setValue('salaryType', 'Bank', {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        })
      }
    }

    if (prefilledData?.fullName) {
      setValue('fullName', prefilledData?.fullName, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      })
    }

    if (prefilledData?.dob) {
      setValue('dob', moment(prefilledData?.dob).format('DD/MM/YYYY'), {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      })
    }
    if (prefilledData?.pincode) {
      setValue('pinCode', prefilledData?.pincode, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      })
    }

    if (prefilledData?.annualIncome) {
      setAnnualIncome(prefilledData?.annualIncome)
      setValue('income', prefilledData?.annualIncome, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      })
    }
    if (prefilledData?.employmentType) {
      setEmploymentType(prefilledData?.employmentType)
      setValue('employmentType', prefilledData?.employmentType, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      })
    }
    if (prefilledData?.modeOfIncome === 'Cash') unregister('bankName')

    if (prefilledData) trigger()
  }, [prefilledData])

  function selectEmploymentType(value: string) {
    setIsEmploymentSheetVisible(false)
    setValue('employmentType', value, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    })
    setEmploymentType(value)
  }

  const { data: EmploymentFields } = useFetchFieldsByCriteria('employmentType')

  const onSubmit = () => {
    const employmentObj = EmploymentFields?.fields?.find(
      (item) => item?.value === getValues('employmentType'),
    )

    const { pinCode, dob, salaryType, fullName, income } = getValues()
    mutate(
      {
        customerId: route.params.customerId,
        employmentType: employmentObj?.key,
        annualIncome: String(income),
        modeOfIncome: salaryType === 'Cash' ? 2 : 1,
        dob: moment(dob, 'DD/MM/YYYY').format('YYYY-MM-DD'),
        pinCode: pinCode,
        fullName: fullName,
      },
      {
        onSuccess: (data) => {
          setError('pinCode', {
            message: data?.message,
          })
        },
      },
    )
  }

  const { mutate, isLoading } = useEditedDetails()

  const onChange = (
    event: DateTimePickerEvent,
    selectedDate: Date | undefined,
  ) => {
    if (event.type === 'dismissed') return
    const value = selectedDate || new Date(MIN_ELIGIBLE_DATE)
    setDate(value)
    setValue('dob', moment(value).format('DD/MM/YYYY'), {
      shouldValidate: true,
      shouldTouch: true,
    })
  }

  const handleEmploymentPress = () => {
    Keyboard.dismiss()
    setIsEmploymentSheetVisible(true)
  }

  const showMode = () => {
    DateTimePickerAndroid.open({
      value: date || undefined,
      onChange,
      mode: 'date',
      maximumDate: new Date(MIN_ELIGIBLE_DATE),
    })
  }

  if (prefilledDataLoading) {
    return (
      <Stack className="flex-1 items-center justify-center bg-white">
        <ActivityIndicator size={'large'} color={zetColors.primary[100]} />
      </Stack>
    )
  }

  return (
    <LazyHOC>
      <KeyBoardWrapper
        className="flex-1 bg-white "
        BottomSheet={
          <BottomView
            onPress={onSubmit}
            idCardPendingScreen={true}
            disabled={!isValid || isLoading}
            loading={isLoading}
            label="Save Changes"
            buttonEvent={{
              eventName: 'click_action',
              payload: {
                item_name: 'brands_list_recommendation_save_customer_details',
              },
            }}
          />
        }
      >
        <Stack className=" px-3">
          <Stack className="mb-1 mt-3">
            <B2 className="text-grays-80  ml-1">
              {i18n.t('knowCustomer.customerName')}
              <B1 className="text-error-100 ml-1">*</B1>
            </B2>
            <Controller
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Full Name is required',
                },
                pattern: {
                  value: NAME_REGEX,
                  message: NAME_FIELD_VALIDATION,
                },
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <Input
                  placeholder="Eg: Aman Singh"
                  onBlur={onBlur}
                  onChangeText={(value) => {
                    if (NAME_REGEX.test(value)) {
                      onChange(value)
                    }
                  }}
                  value={value}
                  type={`${errors.fullName?.message ? 'error' : 'primary'}`}
                  className=" mt-1 "
                />
              )}
              name="fullName"
            />
            {errors.fullName?.message ? (
              <Stack className="mt-2">
                <B5 className="text-error-100">{errors.fullName?.message}</B5>
              </Stack>
            ) : null}
          </Stack>

          <Stack className="mt-3">
            <B2 className=" text-grays-80 mb-1">
              {i18n.t('customerDetails.income')}
              <B1 className="text-error-100 ml-1">*</B1>
            </B2>
            <XStack>
              <Controller
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: 'Mode of Income is required',
                  },
                }}
                render={({ field: { onChange, onBlur } }) => (
                  <XStack>
                    <Pressable
                      onBlur={onBlur}
                      onPress={() => {
                        onChange('Bank')
                        setSalaryType('Bank')
                      }}
                    >
                      <XStack
                        className={`px-4/2 mr-3 items-center rounded-xl border py-2 ${
                          'Bank' !== salaryType
                            ? 'border-grays-40'
                            : 'border-primary-100 bg-grays-20'
                        }`}
                      >
                        {salaryType !== 'Bank' ? (
                          <Icon type="BankAccount" size={36}></Icon>
                        ) : (
                          <Icon type="BankAccountWithColor" size={36}></Icon>
                        )}
                        <B2 className="ml-4/2 text-grays-60">
                          {i18n.t('customerDetails.bank')}
                        </B2>
                      </XStack>
                    </Pressable>
                    <Pressable
                      onBlur={onBlur}
                      onPress={() => {
                        onChange('Cash')
                        setSalaryType('Cash')
                      }}
                    >
                      <XStack
                        className={`px-4/2 mr-3 items-center rounded-xl border py-2
             ${
               'Cash' !== salaryType
                 ? 'border-grays-40'
                 : 'border-primary-100 bg-grays-20'
             }
            `}
                      >
                        {salaryType !== 'Cash' ? (
                          <Icon type="Cash" size={36}></Icon>
                        ) : (
                          <Icon type="CashWithColor" size={36}></Icon>
                        )}
                        <B2 className="ml-4/2 text-grays-60">
                          {i18n.t('customerDetails.cash')}
                        </B2>
                      </XStack>
                    </Pressable>
                  </XStack>
                )}
                name="salaryType"
              />
            </XStack>
            {errors.salaryType?.message ? (
              <Stack className="mt-2">
                <B5 className="text-error-100">{errors.salaryType?.message}</B5>
              </Stack>
            ) : null}
          </Stack>

          {Platform.OS === 'android' ? (
            <Stack className="mt-4">
              <B2 className="text-grays-80 mb-1">
                {i18n.t('customerDetails.dob')}
                <B1 className="text-error-100 ml-1"> *</B1>
              </B2>
              <Controller
                control={control}
                name="dob"
                render={({ field: { onBlur, onChange, value } }) => (
                  <Pressable onPress={showMode}>
                    <Input
                      isEditable={false}
                      placeholder="Select Date"
                      iconRight={
                        <Icon
                          type="ArrowDown"
                          color={zetColors.grays[80]}
                          className="mr-2"
                        />
                      }
                      iconLeft={
                        <Icon
                          type="DayOutlined"
                          color={zetColors.grays[50]}
                          className="mr-2 border"
                        />
                      }
                      className="justify-center"
                      style={{
                        color:
                          date === today
                            ? zetColors.grays[50]
                            : zetColors.grays[100],
                      }}
                      value={value}
                    />
                  </Pressable>
                )}
              />
            </Stack>
          ) : (
            <Stack className="mb-4 mt-4">
              <B2 className="text-grays-80 mb-1">
                {i18n.t('webFlow.basicDetails.dob')}
                <B1 className="text-error-100 ml-1">*</B1>
              </B2>
              <Controller
                control={control}
                name="dob"
                render={({ field: { value, onChange } }) => (
                  <>
                    <Icon
                      type="CalenderOutlined"
                      color={zetColors.grays[50]}
                      className="absolute mt-6 pl-2 pt-1"
                      size={31}
                    ></Icon>
                    {/* {!value && (
                      <Stack className="absolute left-6 mt-6 pt-1">
                        <SH1 className="text-grays-50">DD/MM/YYYY</SH1>
                      </Stack>
                    )} */}
                    <WebDatePicker
                      onChange={(v) => onChange(v)}
                      value={value}
                    />
                  </>
                )}
              />
            </Stack>
          )}

          <Stack>
            <B2 className="text-grays-80 mt-3 mb-1">
              {i18n.t('customerDetails.pin')}
              <B1 className="text-error-100 pl-1">*</B1>
            </B2>

            <Controller
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Pin code is required',
                },

                pattern: {
                  value: PINCODE_REGEX,
                  message: 'Invalid Pin code',
                },
              }}
              render={({
                field: { onChange, onBlur, value },
                fieldState: {},
              }) => (
                <Input
                  iconLeft={
                    <Icon
                      type="LocationPointerOutlined"
                      color={zetColors.grays[50]}
                    ></Icon>
                  }
                  placeholder="Eg: 560035"
                  onBlur={onBlur}
                  onChangeText={(value) => {
                    onChange(value)
                  }}
                  keyboardType="numeric"
                  value={value}
                  maxLength={6}
                />
              )}
              name="pinCode"
            />
            {errors.pinCode?.message ? (
              <Stack className="mt-2">
                <B5 className="text-error-100">{errors.pinCode?.message}</B5>
              </Stack>
            ) : null}
          </Stack>

          <Stack className="mt-3">
            <B2 className="text-grays-80 mb-1">
              {i18n.t('employmentDetails.type')}
              <B1 className="text-error-100 pl-1">*</B1>
            </B2>

            <Controller
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Employment type is required',
                },
              }}
              render={({ field: { onBlur, value } }) => (
                <Pressable onPress={handleEmploymentPress}>
                  <Input
                    isEditable={false}
                    placeholder={i18n.t('employmentDetails.egSelfEmp')}
                    onBlur={onBlur}
                    iconLeft={
                      <Stack className=" ">
                        <Icon type="WorkOutlined" color={zetColors.grays[80]} />
                      </Stack>
                    }
                    iconRight={
                      <Icon
                        type="ArrowDown"
                        color={zetColors.grays[80]}
                        className="mr-1"
                      />
                    }
                    className="justify-center"
                    style={{
                      color: zetColors.grays[100],
                      fontFamily: 'Graphie-Book',
                    }}
                    value={value}
                  />
                </Pressable>
              )}
              name="employmentType"
            />
            {errors.employmentType?.message ? (
              <Stack className="-mt-3 mb-3">
                <B5 className="text-error-100">
                  {errors.employmentType?.message}
                </B5>
              </Stack>
            ) : null}
          </Stack>

          <Stack className="mt-3">
            <B2 className="text-grays-80 mb-1">
              {i18n.t('employmentDetails.annual')}
              <B1 className="text-error-100 pl-1">*</B1>
            </B2>

            <Controller
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Income is required',
                },
                min: {
                  value: 1,
                  message: 'Income is required',
                },
                max: {
                  value: 99999999,
                  message: 'Maximum limit exceeded',
                },

                minLength: 1,
                maxLength: 8,
                pattern: {
                  value: /^(0|[1-9]\d*)(\.\d+)?$/,
                  message: 'Please enter only numbers',
                },
                validate: (value) => value > 0,
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <Input
                  placeholder="Eg: ₹14,475"
                  onBlur={onBlur}
                  onChangeText={(value) => {
                    onChange(+value)
                    setAnnualIncome(value)
                  }}
                  keyboardType="number-pad"
                  value={annualIncome}
                  type={`${errors.income?.message ? 'error' : 'primary'}`}
                />
              )}
              name="income"
            />
            {errors.income?.message ? (
              <Stack className="mt-2">
                <B5 className="text-error-100">{errors.income?.message}</B5>
              </Stack>
            ) : null}
          </Stack>

          <BottomSheet
            visible={isEmploymentSheetVisible}
            disableBottomSheet={() => setIsEmploymentSheetVisible(false)}
            onDismiss={() => setIsEmploymentSheetVisible(false)}
            sheetName="edit_customer_employment_details_type"
          >
            <>
              <XStack className="border-grays-30 justify-between border-b px-3 py-2 ">
                <XStack className=" items-center  ">
                  <Icon type="WorkOutlined" size={24} />
                  <SH1 className=" ml-[12] ">
                    {i18n.t('employmentDetails.type')}
                  </SH1>
                </XStack>
                <Pressable onPress={() => setIsEmploymentSheetVisible(false)}>
                  <Icon size={24} type="Close" />
                </Pressable>
              </XStack>
              <ScrollView className="mb-3">
                <Stack className="px-3">
                  <Stack className="">
                    <Select onChange={selectEmploymentType}>
                      {EmploymentFields?.fields?.map(
                        ({ value, key, displayValue }) => (
                          <Select.Option
                            key={key}
                            name={value}
                            selectedValue={employmentType}
                            optionContainerStyle="h-[62] mt-3 w-full justify-center rounded-lg"
                          >
                            <SH1>{displayValue}</SH1>
                          </Select.Option>
                        ),
                      )}
                    </Select>
                  </Stack>
                </Stack>
              </ScrollView>
            </>
          </BottomSheet>
        </Stack>
      </KeyBoardWrapper>
    </LazyHOC>
  )
}

export default EditCustomerDetails
